import type { DivisionProfile, DivisionBaseline } from '@/lib/types/division-profile';
import { getPercentile } from '@/lib/utils/percentile';

export interface StrengthItem {
  label: string;
  type: 'strength' | 'weakness';
  percentile?: number;
  tooltipText?: string;
}

/** Full roster evaluation for explainer UI: active flags plus neutral bands. */
export interface TraitOutcomeRow {
  key: string;
  kind: 'numeric' | 'binary';
  bucket: 'strength' | 'weakness' | 'neutral';
  label: string;
  detail: string;
  percentile?: number;
}

function buildNumericChecks(
  profile: DivisionProfile,
  sortedValues: DivisionBaseline['sortedValues']
): { key: string; label: (v: number) => string; getValue: () => number }[] {
  return [
    { key: 'sfCount', label: (v) => `${v} SF Squads`, getValue: () => profile.infantry.eliteSfCount },
    { key: 'fwdInfCount', label: (v) => `${v} FWD INF`, getValue: () => profile.infantry.fwdInfCount },
    { key: 'fwdATInfCount', label: (v) => `${v} FWD AT INF`, getValue: () => profile.infantry.fwdATInfCount },
    {
      key: 'heliborneCardCount',
      label: (v) => `${v} Heliborne INF cards`,
      getValue: () => profile.infantry.heliborneCardCount,
    },
    { key: 'atmgSquadCount', label: (v) => `${v} ATGM Squads`, getValue: () => profile.infantry.atmgSquadCount },
    { key: 'atmgInfCount', label: (v) => `${v} ATGM INF`, getValue: () => profile.infantry.atmgInfCount },
    { key: 'highestInfATPen', label: (v) => `INF AT ${v} pen`, getValue: () => profile.infantry.highestInfATPen },
    { key: 'infFieldableModels', label: (v) => `${v} INF Models`, getValue: () => profile.infantry.fieldableInfModels },
    { key: 'infSlots', label: (v) => `${v} INF slots`, getValue: () => profile.infantry.slots.totalSlots },
    {
      key: 'tankScore',
      label: (_v) => `Strong Tanks`,
      getValue: () => {
        if (profile.tanks.mbtModels < 20) return 0;
        const maxAvgPen = sortedValues['tankAvgPen']?.at(-1) ?? 1;
        const maxAvgArmor = sortedValues['tankAvgArmor']?.at(-1) ?? 1;
        const maxAvgAcc = sortedValues['tankAvgAcc']?.at(-1) ?? 1;
        const maxMbtM = sortedValues['tankMbtModels']?.at(-1) ?? 1;
        const avgPen = profile.tanks.tankPenScore / profile.tanks.mbtModels;
        const avgArmor = profile.tanks.tankArmorScore / profile.tanks.mbtModels;
        const avgAcc = profile.tanks.tankAccuracyScore / profile.tanks.mbtModels;
        const quality =
          (avgPen / (maxAvgPen || 1)) + (avgArmor / (maxAvgArmor || 1)) + (avgAcc / (maxAvgAcc || 1));
        const qty = Math.sqrt(profile.tanks.mbtModels / (maxMbtM || 1));
        return quality * 0.75 + qty * 0.75;
      },
    },
    { key: 'tnkSlots', label: (v) => `${v} TNK slots`, getValue: () => profile.tanks.slots.totalSlots },
    { key: 'reconExceptionalOptics', label: (v) => `${v} Exceptional Optics`, getValue: () => profile.recon.exceptionalOpticsCount },
    { key: 'reconTankCount', label: (v) => `${v} Recon tanks`, getValue: () => profile.recon.reconTankCount },
    { key: 'reconAfvCount', label: (v) => `${v} REC AFVs`, getValue: () => profile.recon.reconAfvCount },
    { key: 'radarAACount', label: (v) => `${v} Radar AA`, getValue: () => profile.antiAir.radarAACount },
    { key: 'maxPlaneRange', label: (v) => `AA Range ${v}m`, getValue: () => profile.antiAir.maxPlaneRange },
    { key: 'artSlots', label: (v) => `${v} ART slots`, getValue: () => profile.artillery.slots.totalSlots },
    {
      key: 'lightAttackHeliCount',
      label: (v) => `${v} Light attack helis`,
      getValue: () => profile.helicopters.lightAttackHeliCount,
    },
    { key: 'gunshipCount', label: (v) => `${v} Gunships`, getValue: () => profile.helicopters.gunshipCount },
    {
      key: 'heliPackCount',
      label: (v) => `${v} HEL packs`,
      getValue: () => profile.helicopters.packCount,
    },
    { key: 'airSlots', label: (v) => `${v} AIR slots`, getValue: () => profile.planes.slots.totalSlots },
    {
      key: 'planePackCount',
      label: (v) => `${v} plane types`,
      getValue: () => profile.planes.packCount,
    },
    { key: 'maxAsfMissileRange', label: (v) => `ASF AAM ${v}m`, getValue: () => profile.planes.maxAsfMissileRange },
  ];
}

function weakNumericLabel(
  key: string,
  value: number,
  labelFn: (v: number) => string
): string {
  if (key === 'maxPlaneRange') return `Weak AA (${value}m)`;
  if (key === 'maxAsfMissileRange') return `Short ASF AAM (${value}m)`;
  if (key === 'tankScore') return 'Weak Tanks';
  if (key === 'planePackCount') return `Thin air (${value} types)`;
  if (key === 'airSlots') return `Few AIR slots (${value})`;
  if (key === 'heliPackCount') return `Few HEL packs (${value})`;
  return labelFn(value);
}

/**
 * Every numeric and binary trait with strength / weakness / neutral outcome for this division.
 * Neutral = not in the top/bottom quartile (numeric) or does not meet binary rarity rules.
 */
export function deriveTraitOutcomeRows(
  profile: DivisionProfile,
  baseline: DivisionBaseline
): TraitOutcomeRow[] {
  const rows: TraitOutcomeRow[] = [];
  const { sortedValues, binaryRates } = baseline;

  if (Object.keys(sortedValues).length === 0) return rows;

  const numericChecks = buildNumericChecks(profile, sortedValues);

  for (const check of numericChecks) {
    const sorted = sortedValues[check.key];
    if (!sorted) continue;
    const value = check.getValue();
    const pct = getPercentile(sorted, value);
    const rounded = Math.round(pct);
    const label = check.label(value);

    if (pct >= 75) {
      rows.push({
        key: check.key,
        kind: 'numeric',
        bucket: 'strength',
        label,
        percentile: rounded,
        detail: `Top ${100 - rounded}% — higher than ${rounded}% of divisions (≥75th percentile). Other divisions sit lower on this metric.`,
      });
    } else if (pct <= 25) {
      const wl = weakNumericLabel(check.key, value, check.label);
      rows.push({
        key: check.key,
        kind: 'numeric',
        bucket: 'weakness',
        label: wl,
        percentile: rounded,
        detail: `Bottom ${rounded}% — lower than ${100 - rounded}% of divisions (≤25th percentile). Other divisions rank higher here.`,
      });
    } else {
      rows.push({
        key: check.key,
        kind: 'numeric',
        bucket: 'neutral',
        label,
        percentile: rounded,
        detail: `~${rounded}th percentile — between strength (≥75th) and weakness (≤25th) bands for this roster. Not flagged on the summary pills.`,
      });
    }
  }

  const binaryChecks: { key: string; label: string; has: boolean }[] = [
    { key: 'hasIFV', label: 'IFVs', has: profile.infantry.ifvCount > 0 },
    { key: 'hasJammer', label: 'Jammer', has: profile.recon.hasJammer },
    { key: 'hasSupplyHeli', label: 'Supply Heli', has: profile.logistics.hasSupplyHeli },
    { key: 'hasReconPlane', label: 'Recon Plane/UAV', has: profile.recon.hasReconPlane },
    { key: 'hasReconTank', label: 'Recon tanks', has: profile.recon.reconTankCount > 0 },
    { key: 'has30PenAT', label: '30+ Pen AT', has: profile.overview.has30PenAT },
    { key: 'hasReconHeli', label: 'Recon Heli', has: profile.recon.hasReconHeli },
    { key: 'hasASF', label: 'ASF', has: profile.planes.asfCount > 0 },
    { key: 'hasSEAD', label: 'SEAD', has: profile.planes.seadCount > 0 },
    { key: 'hasLGB', label: 'LGB', has: profile.planes.lgbCount > 0 },
    { key: 'hasEW', label: 'EW Plane', has: profile.planes.ewCount > 0 },
    { key: 'hasCluster', label: 'Cluster Arty', has: profile.artillery.clusterMLRSCount > 0 },
    { key: 'hasNapalm', label: 'Napalm Arty', has: profile.artillery.napalmMLRSCount > 0 },
    { key: 'hasThermobaric', label: 'Thermobaric Arty', has: profile.artillery.thermobaricCount > 0 },
    { key: 'hasSigint', label: 'SIGINT', has: profile.overview.hasSigint },
    {
      key: 'hasHeliborneInf',
      label: 'Heliborne INF',
      has: profile.infantry.heliborneCardCount > 0,
    },
    {
      key: 'hasActiveProtection',
      label: 'Active Protection',
      has: profile.tanks.activeProtectionCardCount > 0,
    },
  ];

  for (const check of binaryChecks) {
    const rate = binaryRates[check.key] ?? 0.5;
    const pctHave = Math.round(rate * 100);
    if (check.has && rate < 0.4) {
      rows.push({
        key: check.key,
        kind: 'binary',
        bucket: 'strength',
        label: check.label,
        detail: `Uncommon strength — only ${pctHave}% of divisions have this.`,
      });
    } else if (!check.has && rate > 0.6) {
      rows.push({
        key: check.key,
        kind: 'binary',
        bucket: 'weakness',
        label: `No ${check.label}`,
        detail: `Common gap — ${pctHave}% of divisions have this.`,
      });
    } else if (check.has) {
      rows.push({
        key: check.key,
        kind: 'binary',
        bucket: 'neutral',
        label: check.label,
        detail: `You have this; at least 40% of divisions also do (${pctHave}%), so it is not an “uncommon” strength. Decks without it may show a weakness if most others field it.`,
      });
    } else {
      rows.push({
        key: check.key,
        kind: 'binary',
        bucket: 'neutral',
        label: `No ${check.label}`,
        detail: `You lack this; at most 60% of divisions have it (${pctHave}%), so not a “common gap” weakness. Decks that include it may earn an uncommon strength if ownership is rare.`,
      });
    }
  }

  return rows;
}

export function deriveStrengthsAndWeaknesses(
  profile: DivisionProfile,
  baseline: DivisionBaseline
): StrengthItem[] {
  const items: StrengthItem[] = [];
  const { sortedValues, binaryRates } = baseline;

  if (Object.keys(sortedValues).length === 0) return items;

  const numericChecks = buildNumericChecks(profile, sortedValues);

  for (const check of numericChecks) {
    const sorted = sortedValues[check.key];
    if (!sorted) continue;
    const value = check.getValue();
    const pct = getPercentile(sorted, value);
    const rounded = Math.round(pct);
    if (pct >= 75) {
      items.push({
        label: check.label(value),
        type: 'strength',
        percentile: rounded,
        tooltipText: `Top ${100 - rounded}% — higher than ${rounded}% of divisions`,
      });
    } else if (pct <= 25) {
      const weakLabel = weakNumericLabel(check.key, value, check.label);
      items.push({
        label: weakLabel,
        type: 'weakness',
        percentile: rounded,
        tooltipText: `Bottom ${rounded}% — lower than ${100 - rounded}% of divisions`,
      });
    }
  }

  const binaryChecks: { key: string; label: string; has: boolean }[] = [
    { key: 'hasIFV', label: 'IFVs', has: profile.infantry.ifvCount > 0 },
    { key: 'hasJammer', label: 'Jammer', has: profile.recon.hasJammer },
    { key: 'hasSupplyHeli', label: 'Supply Heli', has: profile.logistics.hasSupplyHeli },
    { key: 'hasReconPlane', label: 'Recon Plane/UAV', has: profile.recon.hasReconPlane },
    { key: 'hasReconTank', label: 'Recon tanks', has: profile.recon.reconTankCount > 0 },
    { key: 'has30PenAT', label: '30+ Pen AT', has: profile.overview.has30PenAT },
    { key: 'hasReconHeli', label: 'Recon Heli', has: profile.recon.hasReconHeli },
    { key: 'hasASF', label: 'ASF', has: profile.planes.asfCount > 0 },
    { key: 'hasSEAD', label: 'SEAD', has: profile.planes.seadCount > 0 },
    { key: 'hasLGB', label: 'LGB', has: profile.planes.lgbCount > 0 },
    { key: 'hasEW', label: 'EW Plane', has: profile.planes.ewCount > 0 },
    { key: 'hasCluster', label: 'Cluster Arty', has: profile.artillery.clusterMLRSCount > 0 },
    { key: 'hasNapalm', label: 'Napalm Arty', has: profile.artillery.napalmMLRSCount > 0 },
    { key: 'hasThermobaric', label: 'Thermobaric Arty', has: profile.artillery.thermobaricCount > 0 },
    { key: 'hasSigint', label: 'SIGINT', has: profile.overview.hasSigint },
    {
      key: 'hasHeliborneInf',
      label: 'Heliborne INF',
      has: profile.infantry.heliborneCardCount > 0,
    },
    {
      key: 'hasActiveProtection',
      label: 'Active Protection',
      has: profile.tanks.activeProtectionCardCount > 0,
    },
  ];

  for (const check of binaryChecks) {
    const rate = binaryRates[check.key] ?? 0.5;
    const pctHave = Math.round(rate * 100);
    if (check.has && rate < 0.4) {
      items.push({
        label: check.label,
        type: 'strength',
        tooltipText: `Uncommon — only ${pctHave}% of divisions have this`,
      });
    } else if (!check.has && rate > 0.6) {
      items.push({
        label: `No ${check.label}`,
        type: 'weakness',
        tooltipText: `Common gap — ${pctHave}% of divisions have this`,
      });
    }
  }

  return items;
}
